import moment from 'moment';

export const PINNED_BADGE_VARIABLES = ['createdAt', 'updatedAt'];

export function isBadgeVariable(name) {
  return name && !name.startsWith('fileName-');
}

export function buildBadgeVariableNames(apiColumns) {
  const names = apiColumns.filter((c) => isBadgeVariable(c));
  ['chatProgressId', '_id'].forEach((sys) => {
    if (!names.includes(sys)) names.push(sys);
  });
  return names.filter((v) => !PINNED_BADGE_VARIABLES.includes(v));
}

export function dataFieldToVariable(dataField) {
  if (!dataField) return '';
  if (dataField.startsWith('data.')) return dataField.slice(5);
  return dataField;
}

export function getRowVariableValue(row, variableName) {
  if (variableName === 'chatProgressId') return row.chatProgressId;
  if (variableName === 'createdAt') return row.createdAt;
  if (variableName === 'updatedAt') return row.updatedAt;
  if (variableName === '_id') return row._id;
  return row.data?.[variableName];
}

export function hasNonEmptyValue(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}

export function formatCellValue(row, column, chatbotId) {
  const { dataField } = column;
  if (dataField === 'createdAt') {
    return row.createdAt ? moment(row.createdAt).format('D.M.YYYY HH:mm') : '';
  }
  if (dataField === 'updatedAt') {
    return row.updatedAt ? moment(row.updatedAt).format('D.M.YYYY HH:mm') : '';
  }
  if (dataField === 'chatProgressId') {
    return row.chatProgressId
      ? `https://app.hugo.legal/hugoapp?bot=${chatbotId}&pid=${row.chatProgressId}`
      : '';
  }
  if (dataField === 'booking' || dataField === 'data.booking') {
    const booking = row.data?.booking;
    return booking
      ? `EventId: ${booking.event_id || ''} | ProjectId: ${booking.project_id || ''}`
      : '';
  }
  const variable = dataFieldToVariable(dataField);
  const value = getRowVariableValue(row, variable);
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function applyPeriodFilter(data, period) {
  if (!period || period === 'all') return data;

  let start;
  let end;

  if (period === 'this_week') {
    start = moment().startOf('isoWeek');
    end = moment().endOf('isoWeek');
  } else if (period === 'last_week') {
    start = moment().subtract(1, 'week').startOf('isoWeek');
    end = moment().subtract(1, 'week').endOf('isoWeek');
  } else if (period.startsWith('month:')) {
    const [, ym] = period.split(':');
    start = moment(ym, 'YYYY-MM').startOf('month');
    end = moment(ym, 'YYYY-MM').endOf('month');
  } else {
    return data;
  }

  return data.filter((row) => {
    const activityAt = row.updatedAt || row.createdAt;
    if (!activityAt) return false;
    return moment(activityAt).isBetween(start, end, undefined, '[]');
  });
}

export function sanitizePeriod(period, periodOptions) {
  const valid = new Set(periodOptions.map((o) => o.value));
  return valid.has(period) ? period : 'all';
}

export function buildPeriodOptions(locale) {
  const loc = locale === 'et' ? 'et-EE' : 'en-US';
  const options = [
    { value: 'all', labelKey: 'conversation.periodAll' },
    { value: 'this_week', labelKey: 'conversation.periodThisWeek' },
    { value: 'last_week', labelKey: 'conversation.periodLastWeek' },
  ];

  for (let i = 0; i < 12; i += 1) {
    const m = moment().subtract(i, 'months');
    const value = `month:${m.format('YYYY-MM')}`;
    const label = m.toDate().toLocaleString(loc, { month: 'long', year: 'numeric' });
    options.push({ value, label });
  }

  return options;
}

export function applyHasDataFilter(data, hasDataVars) {
  if (!hasDataVars?.length) return data;
  return data.filter((row) =>
    hasDataVars.every((variable) => hasNonEmptyValue(getRowVariableValue(row, variable))),
  );
}

export function applySearch(data, search, visibleColumns, chatbotId) {
  if (!search?.trim()) return data;
  const q = search.trim().toLowerCase();
  return data.filter((row) =>
    visibleColumns.some((col) =>
      formatCellValue(row, col, chatbotId).toLowerCase().includes(q),
    ),
  );
}

export function isColumnVisible(column, hiddenColumns) {
  const variable = dataFieldToVariable(column.dataField);
  return !hiddenColumns.includes(variable);
}

export function applyColumnOrder(variableNames, columnOrder) {
  if (!columnOrder?.length) return variableNames;
  const ordered = columnOrder.filter((v) => variableNames.includes(v));
  const rest = variableNames.filter((v) => !columnOrder.includes(v));
  return [...ordered, ...rest];
}

export function sanitizeHiddenColumns(hiddenColumns, variableNames = []) {
  let sanitized = hiddenColumns.filter((v) => !PINNED_BADGE_VARIABLES.includes(v));
  if (variableNames.length) {
    const badgeVars = variableNames.filter((v) => !PINNED_BADGE_VARIABLES.includes(v));
    sanitized = sanitized.filter((v) => badgeVars.includes(v));
    const visibleBadgeCount = badgeVars.filter((v) => !sanitized.includes(v)).length;
    if (badgeVars.length > 0 && visibleBadgeCount === 0) {
      return [];
    }
  }
  return sanitized;
}

export function buildOrderedColumns(allColumns, variableNames, columnOrder, hiddenColumns) {
  const colByVar = Object.fromEntries(
    allColumns.map((c) => [dataFieldToVariable(c.dataField), c]),
  );
  const pinnedCols = PINNED_BADGE_VARIABLES
    .filter((v) => colByVar[v])
    .map((v) => colByVar[v]);
  const badgeVars = variableNames.filter((v) => !PINNED_BADGE_VARIABLES.includes(v));
  const orderedVars = applyColumnOrder(badgeVars, columnOrder);
  const toggledCols = orderedVars
    .filter((v) => colByVar[v])
    .filter((v) => !hiddenColumns.includes(v))
    .map((v) => colByVar[v]);
  return [...pinnedCols, ...toggledCols];
}

export function filterActiveDateColumn(columns, showActivity) {
  const active = showActivity ? 'updatedAt' : 'createdAt';
  return columns.filter((col) => {
    const v = dataFieldToVariable(col.dataField);
    if (v === 'createdAt' || v === 'updatedAt') return v === active;
    return true;
  });
}

export function isDateColumn(dataField) {
  return dataField === 'createdAt' || dataField === 'updatedAt';
}

export function getBadgeClass(variable, hiddenColumns, hasDataVars, { highlighted = false } = {}) {
  if (highlighted) return 'variable-badge is-has-data';
  if (hasDataVars.includes(variable)) return 'variable-badge is-has-data';
  if (hiddenColumns.includes(variable)) return 'variable-badge is-off';
  return 'variable-badge is-on';
}

export function isGraphErrorColumn(variable) {
  return String(variable || '').toLowerCase() === 'error';
}

export function cellHasGraphContent(row, column, chatbotId) {
  const variable = dataFieldToVariable(column.dataField);
  return hasNonEmptyValue(getRowVariableValue(row, variable));
}

export function getGraphColumnVariables(visibleColumns) {
  return visibleColumns
    .map((c) => dataFieldToVariable(c.dataField))
    .filter((v) => v && !isDateColumn(v) && v !== '__actions__');
}

export function getNextGraphColumnVariable(visibleColumns, currentVariable) {
  const vars = getGraphColumnVariables(visibleColumns);
  const idx = vars.indexOf(currentVariable);
  if (idx === -1 || idx >= vars.length - 1) return null;
  return vars[idx + 1];
}

export function isGraphDropoffRow(row, currentVariable, nextVariable) {
  if (!nextVariable) return false;
  if (!hasNonEmptyValue(getRowVariableValue(row, currentVariable))) return false;
  return !hasNonEmptyValue(getRowVariableValue(row, nextVariable));
}

export function getBadgeLabel(variable, t) {
  if (variable === 'createdAt') return t('conversation.dateTime');
  if (variable === 'updatedAt') return t('conversation.updated');
  return variable;
}

export function formatRowDataSummary(row, columns, chatbotId) {
  return columns
    .filter((col) => col.dataField !== '__actions__')
    .map((col) => {
      const value = formatCellValue(row, col, chatbotId);
      if (!value) return null;
      return `<strong>${col.text}:</strong> ${value}`;
    })
    .filter(Boolean)
    .join('<br/>');
}

export function buildDetailGridTemplate(detailColumnCount, hasActions) {
  const parts = ['150px'];
  if (detailColumnCount > 0) {
    parts.push(`repeat(${detailColumnCount}, minmax(50px, 1fr))`);
  }
  if (hasActions) {
    parts.push('100px');
  }
  return parts.join(' ');
}

export function getActiveDateField(showActivity) {
  return showActivity ? 'updatedAt' : 'createdAt';
}

export function compareConversationRows(a, b, sortField, sortOrder, chatbotId) {
  let cmp = 0;
  if (sortField === 'createdAt' || sortField === 'updatedAt') {
    const aTime = a[sortField] ? new Date(a[sortField]).getTime() : 0;
    const bTime = b[sortField] ? new Date(b[sortField]).getTime() : 0;
    cmp = aTime - bTime;
  } else {
    const aVal = formatCellValue(a, { dataField: sortField }, chatbotId);
    const bVal = formatCellValue(b, { dataField: sortField }, chatbotId);
    cmp = aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
  }
  return sortOrder === 'asc' ? cmp : -cmp;
}

export function normalizeLeadEmail(email) {
  if (!email || typeof email !== 'string') return '';
  const lower = email.toLowerCase().trim();
  const [localPart, domain] = lower.split('@');
  if (domain === 'gmail.com') {
    const normalizedLocalPart = localPart.split('+')[0].replace(/\./g, '');
    return `${normalizedLocalPart}@${domain}`;
  }
  return lower;
}

export function dedupeRowsByEmail(rows) {
  const byKey = new Map();
  rows.forEach((row) => {
    const email = normalizeLeadEmail(row.data?.email);
    const key = email || `__id:${row._id}`;
    const rowTime = new Date(row.updatedAt || row.createdAt || 0).getTime();
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, row);
      return;
    }
    const existingTime = new Date(existing.updatedAt || existing.createdAt || 0).getTime();
    if (rowTime > existingTime) {
      byKey.set(key, row);
    }
  });
  return Array.from(byKey.values());
}

export function getConversationAvailableDates(rawData) {
  const dates = new Set();
  rawData.forEach((row) => {
    if (row.createdAt) dates.add(moment(row.createdAt).format('YYYY-MM-DD'));
  });
  const sorted = [...dates].sort();
  return {
    dates: sorted,
    dateSet: dates,
    minDate: sorted[0] || '',
    maxDate: sorted[sorted.length - 1] || '',
  };
}
